import React from 'react';
import { useForm } from 'react-hook-form';
import { ErrorMessage } from 'components';
import { Button } from 'design-system-web';
import { HookFormInput, HookFormTextarea } from 'components/reactHookForm';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import styles from '../styles.scss';

export type CategoryFormData = {
  name: string;
  description: string;
}

export type CategoryFormProps = {
  productCategory?: {
    name?: string;
    description?: string;
  };
  error?: {
    severity: 'warning' | 'error';
    error: Error;
  };
  disabled?: boolean;
  onSubmit: (data: CategoryFormData) => Promise<void>;
  onCancel: () => void;
}

export function CategoryForm({
  productCategory,
  error,
  disabled,
  onSubmit,
  onCancel,
}: CategoryFormProps) {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    watch,
  } = useForm<CategoryFormData>({
    mode: 'onChange',
    values: {
      name: productCategory?.name || '',
      description: productCategory?.description || '',
    },
  });

  const watchedCategoryName = watch('name');
  const isSaveDisabled = !watchedCategoryName?.trim() || disabled || (error?.severity === 'error');

  return (
    <>
      <div className={styles.editCategoryForm}>
        <fieldset className={cx(styles.fieldset, styles.categoryName)}>
          <label className={styles.editLabel}>
            {t('abp.productsServices.editCategory.name')}
            <span className={styles.InputLabelRequiredAstrisk}>*</span>
          </label>
          <HookFormInput
            control={control}
            name="name"
            type="text"
            isRequired
            testId="categoryName"
            disabled={error?.severity === 'error'}
          />
        </fieldset>

        <fieldset className={styles.fieldset}>
          <label className={styles.editLabel}>
            {t('abp.productsServices.editCategory.description')}
          </label>
          <HookFormTextarea
            control={control}
            name="description"
            maxLength={200}
            showLengthIndicator
            minRows={4}
            disabled={error?.severity === 'error'}
            containerClassName={error?.severity === 'error' ? styles.disabledTextarea : undefined}
          />
        </fieldset>
      </div>

      {error && <ErrorMessage error={error.error} />}

      <div className={styles.formButtons}>
        <Button
          className={styles.button}
          variant="primary"
          size="large"
          disabled={isSaveDisabled}
          onClick={handleSubmit(onSubmit)}
        >
          {t('abp.productsServices.editCategory.save')}
        </Button>
        <Button
          className={styles.button}
          variant="secondary"
          size="large"
          onClick={onCancel}
          disabled={disabled}
        >
          {t('abp.productsServices.editCategory.cancel')}
        </Button>
      </div>
    </>
  );
}
